// SalesByHourReport.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
// --- HELPERS -------------------------------
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd

const COLUMNS = [
  { key: 'Hour', label: 'Hour' },
  { key: 'PeriodDisplay', label: 'Date' },
  { key: 'Transactions', label: 'Transactions', align: 'right' },
  { key: 'TotalQty', label: 'Total Sales (Qty)', align: 'right' },
  { key: 'TotalAmount', label: 'Total Sales (Amount)', align: 'right' },
];

// convert "7:00 AM" / "13:00 PM" → minutes since midnight, for proper sort
const hourToMinutes = (str) => {
  if (!str) return 0;
  const m = String(str).match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!m) return 0;

  let [, h, min, ampm] = m;
  let hour = parseInt(h, 10) || 0;
  const minutes = parseInt(min, 10) || 0;

  if (ampm) {
    const isPM = ampm.toUpperCase() === 'PM';
    if (isPM && hour !== 12) hour += 12;
    if (!isPM && hour === 12) hour = 0; // 12 AM
  }

  // handles weird "13:00 PM" → behaves like "1:00 PM"
  if (!ampm && hour >= 24) hour = hour % 24;

  return hour * 60 + minutes;
};

// --- COMPONENT -------------------------------
const SalesByHourReport = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 100, // or whatever default you want
    totalRecords: 0,
    totalPages: 0,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  // ------------------------------------------------------------
  // Fetch Branches
  // ------------------------------------------------------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];

      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ------------------------------------------------------------
  // Fetch Sales By Hour Data
  // ------------------------------------------------------------
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // If the backend supports paging, uncomment these:
      // params.append('PageNo', page);
      // params.append('PageSize', size);

      params.append('ReportType', 'Sales By Hour'); // ⭐ important
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');
      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      // 🔹 Normalize keys from API into JS-friendly ones
      const normalized = result.map((r) => ({
        Hour: r.Hour || r['Hour'] || '—',
        // handle different backend key styles like "PeriodDate", "Period Date", "Period"
        PeriodDate: r.PeriodDisplay || r['PeriodDisplay'] || r['Period'] || '—',
        Transactions: +r.Transactions || 0,
        TotalQty: +r['Total Sales (Qty)'] || 0,
        TotalAmount: +r['Total Sales (Amount)'] || 0,
      }));

      // 🔹 Group by (PeriodDate + Hour)
      const groupedMap = new Map();

      normalized.forEach((row) => {
        const dateKey = row.PeriodDate || '—';
        const hourKey = row.Hour || '—';
        const key = `${dateKey}__${hourKey}`; // unique per (date, hour)

        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            Hour: hourKey,
            PeriodDate: dateKey,
            Transactions: 0,
            TotalQty: 0,
            TotalAmount: 0,
          });
        }

        const agg = groupedMap.get(key);
        agg.Transactions += row.Transactions;
        agg.TotalQty += row.TotalQty;
        agg.TotalAmount += row.TotalAmount;
      });

      const grouped = Array.from(groupedMap.values());

      setRows(grouped);

      setPagination((p) => ({
        ...p,
        ...(res?.data?.paginationData || {}),
        totalRecords: grouped.length,
        currentPage: page,
        pageSize: size,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Load branches once
  useEffect(() => {
    fetchBranches();
  }, []);

  // ------------------------------------------------------------
  // Sorting Logic
  // ------------------------------------------------------------
  const [sortState, setSortState] = useState({ key: 'Hour', dir: 'asc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      // special sort for Hour column
      if (key === 'Hour') {
        const am = hourToMinutes(av);
        const bm = hourToMinutes(bv);
        return dir === 'asc' ? am - bm : bm - am;
      }

      // numeric sort if both numeric
      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? av - bv : bv - av;
      }

      // fallback string
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  // Totals row
  const totals = useMemo(
    () => ({
      trans: sortedRows.reduce((s, r) => s + r.Transactions, 0),
      qty: sortedRows.reduce((s, r) => s + r.TotalQty, 0),
      amt: sortedRows.reduce((s, r) => s + r.TotalAmount, 0),
    }),
    [sortedRows],
  );

  // Handlers
  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows
      .map((r) =>
        COLUMNS.map((c) => {
          const val = r[c.key];
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(','),
      )
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-by-hour-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const exportExcel = () => {
    if (!sortedRows.length) return;

    // Header
    const header = COLUMNS.map((c) => c.label);

    // Body (numbers as numbers)
    const body = sortedRows.map((r) =>
      COLUMNS.map((c) => {
        const v = r?.[c.key];

        if (c.key === 'Transactions') return Number(v) || 0;
        if (c.key === 'TotalQty') return Number(v) || 0;
        if (c.key === 'TotalAmount') return Number(v) || 0;

        return String(v ?? '').trim(); // Hour, Date
      }),
    );

    // Totals row (like footer)
    const totalsRow = ['Total', '', Number(totals.trans) || 0, Number(totals.qty) || 0, Number(totals.amt) || 0];

    const aoa = [header, ...body, totalsRow];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths
    ws['!cols'] = [
      { wch: 12 }, // Hour
      { wch: 14 }, // Date
      { wch: 14 }, // Transactions
      { wch: 16 }, // Total Sales (Qty)
      { wch: 20 }, // Total Sales (Amount)
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales By Hour');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `sales-by-hour_${branchName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* <h4 className="mb-3">Sales By Hour</h4> */}

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    <td>{r.Hour}</td>
                    <td>{r.PeriodDate}</td>
                    <td className="text-end">{r.Transactions}</td>
                    <td className="text-end">{r.TotalQty}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.TotalAmount)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  <td></td>
                  <td className="text-end">{totals.trans}</td>
                  <td className="text-end">{totals.qty}</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.amt)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(SalesByHourReport);
